import type { JSX } from 'react'
import { useBrand } from '@/brand/context'
import { PublicPage } from './PublicPage'

/**
 * Nobody's workspace (spec: *Hosts*).
 *
 * `GET /api/v1/branding` answers `host: 'unknown'` for a tenant-shaped host
 * nobody owns — the retired `app.` alias, a mistyped subdomain, a slug that
 * was never registered. `RequireTenantHost` used to treat this the same as
 * the apex, which drew the apex's own login screen — AugRank's bullets,
 * *Start a free trial* — for a host where `POST /auth/login` can only ever
 * answer 404 `HOST_UNKNOWN`. Signing in was never possible here; the old
 * screen just did not say so.
 *
 * So this stands in its place at the root of the route table (`AppRoutes`),
 * ahead of all five bands, rather than behind a guard further in: `/login`,
 * `/dashboard`, a stale bookmark, all show the same sentence, because none of
 * them has anything to offer on a host that answers to no organisation.
 *
 * The split shell, but neither brand: `BrandPanel` draws no pitch and no
 * tenant mark for `host === 'unknown'` — this is not AugRank selling itself
 * and not an organisation's page, so it gets the mark alone.
 */
export function NoWorkspace(): JSX.Element {
  const brand = useBrand()
  return (
    <PublicPage title="Workspace not found">
      <div className="ar-card__body ar-stack">
        <p style={{ marginTop: 0 }}>There is no workspace at this address.</p>
        {/* An absolute, cross-origin link rather than `Link`: the apex is a
            different host, and `react-router`'s `Link` would try to resolve
            it against this one. */}
        <p className="ar-muted">
          Check the address, or <a href={`${brand.apexUrl}/login`}>sign in at {brand.apexUrl}</a>.
        </p>
      </div>
    </PublicPage>
  )
}
